import React, { useState, useEffect } from "react";
import {
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { object, string, email, type infer as Infer } from "zod";
import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { Button } from "components/common/Button";
import { createLogger } from "debug/DebugingLogger";
import { useAuth } from "contexts/AuthProvider";
import { Tooltip } from "@/components/common/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { AuthHeader } from "@/components/authentication/common/AuthHeader";
import { AuthStatusBanner } from "@/components/authentication/common/AuthStatusBanner";
import { GoogleSignInButton } from "@/components/authentication/common/GoogleSignInButton";
import { isPasswordValid } from "@/components/authentication/common/passwordValidation";
const log = createLogger("RegisterPage");

const registerSchema = object({
  name: string().min(1, "Full name is required"),
  email: email("Email Address is invalid").min(1, "Email address is required"),
  password: string()
    .min(1, "Password is required")
    .min(8, "Password must be more than 8 characters")
    .max(32, "Password must be less than 32 characters")
    .refine(isPasswordValid, "Password is not strong enough"),
});

export type RegisterInput = Infer<typeof registerSchema>;

export const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { authenticated } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [atCapacity, setAtCapacity] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState<string | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  // null while unknown — redirecting before the answer arrives would bounce every
  // visitor off the page on a slow request.
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(
    null,
  );
  const from =
    ((location.state as { from?: { pathname?: string } })?.from
      ?.pathname as string) || "/overview";

  useEffect(() => {
    // Check for error message in URL params (from OAuth redirect)
    const errorFromUrl = searchParams.get("error");
    if (errorFromUrl) {
      setErrorMessage(decodeURIComponent(errorFromUrl));
      // Clear the error from URL after displaying it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true },
      );
    }
  }, [searchParams, navigate, location.pathname]);

  useEffect(() => {
    axiosInstance
      .get("/api/auth/registration-status")
      .then((res) => setRegistrationOpen(!!res.data?.registration_open))
      .catch(() => setRegistrationOpen(false));
  }, []);

  // Look up email from verification code (approval link)
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code) return;
    setCodeLoading(true);
    axiosInstance
      .get(`/api/auth/verify-code/${code}`)
      .then((res) => {
        if (res.data?.email) {
          setPrefillEmail(res.data.email);
          if (res.data?.expires_at) {
            setCodeExpiresAt(res.data.expires_at);
          }
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Invalid registration link.";
        setErrorMessage(msg);
      })
      .finally(() => setCodeLoading(false));
  }, [searchParams]);

  const registerUser = async (data: RegisterInput) => {
    try {
      // Code from the invite email link — presenting it auto-verifies.
      const verificationCode = searchParams.get("code");
      const payload: Record<string, string> = { ...data };
      if (verificationCode) {
        payload.verification_code = verificationCode;
      }
      const response = await axiosInstance.post(`/api/auth/register`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.data?.status === "waitlisted") {
        setSuccessMessage(response.data.message);
        setErrorMessage("");
        return;
      }
      if (response.data?.status === "verified") {
        // Auto-verified via the invite code — go straight to login
        navigate("/?verified=true");
        return;
      }
      if (response.status === 200) {
        navigate("/verification-sent");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.status === "capacity_reached") {
          setAtCapacity(true);
          setErrorMessage("");
          return;
        }
        const resMessage =
          (error.response?.data?.message as string) || error.message;
        setErrorMessage(resMessage);
        setSuccessMessage("");
        log.error(resMessage);
      } else {
        const resMessage =
          error instanceof Error ? error.message : "Registration failed";
        setErrorMessage(resMessage);
        setSuccessMessage("");
        log.error(resMessage);
      }
    }
  };

  const methods = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = methods;

  // Pre-fill email when verification code lookup succeeds
  useEffect(() => {
    if (prefillEmail) {
      setValue("email", prefillEmail);
    }
  }, [prefillEmail, setValue]);

  const onSubmitHandler: SubmitHandler<RegisterInput> = (values) => {
    setErrorMessage("");
    registerUser(values);
  };

  if (authenticated) {
    return <Navigate to="/overview" />;
  }

  // Invite-only: /register is reachable only via an approval link. Open sign-up
  // drops that, so the code becomes an optional prefill rather than the entry ticket.
  if (registrationOpen === null) {
    return null;
  }
  if (!registrationOpen && !searchParams.get("code")) {
    return <Navigate to="/" replace />;
  }

  if (codeLoading) {
    return (
      <section className="auth-page">
        <div className="flex flex-col justify-center items-center min-h-full px-6 py-12 lg:px-8">
          <div className="w-full sm:max-w-sm flex flex-col gap-8">
            <AuthHeader title="Sign up" />
            <p style={{ color: "var(--color-fg-muted)", textAlign: "center" }}>
              Verifying your registration link...
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Terminal, not a validation error — re-submitting the form cannot clear it.
  if (atCapacity) {
    return (
      <section className="auth-page">
        <div className="flex flex-col justify-center items-center min-h-full px-6 py-12 lg:px-8">
          <div className="w-full sm:max-w-sm flex flex-col gap-8">
            <AuthHeader title="Oh no — we're full" />
            <AuthStatusBanner
              message="MyCorr has hit its account limit, so we can't create new accounts right now. If someone invited you, ask them to resend your registration link — invitations aren't affected by the limit."
              variant="info"
            />
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate("/")}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-page">
      <div className="flex flex-col justify-center items-center min-h-full px-6 py-12 lg:px-8">
        <div className="w-full sm:max-w-sm flex flex-col gap-8">
          <AuthHeader title="Sign up" />

          <GoogleSignInButton from={from} />

          <div className="auth-divider">
            <hr />
            <span>Or</span>
            <hr />
          </div>

          <form
            noValidate
            onSubmit={handleSubmit(onSubmitHandler)}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-4">
              <Tooltip
                title={errors.name?.message || ""}
                open={!!errors.name}
                placement="top"
              >
                <input
                  id="name"
                  type="text"
                  className="auth-input"
                  placeholder="Full Name"
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
              </Tooltip>
              <Tooltip
                title={errors.email?.message || ""}
                open={!!errors.email}
                placement="top"
              >
                <input
                  id="email"
                  type="email"
                  className="auth-input"
                  placeholder="Email address"
                  aria-invalid={!!errors.email}
                  readOnly={!!prefillEmail}
                  style={
                    prefillEmail
                      ? { opacity: 0.7, cursor: "default" }
                      : undefined
                  }
                  {...register("email")}
                />
              </Tooltip>
              {codeExpiresAt && (
                <p
                  style={{
                    color: "var(--color-fg-muted)",
                    fontSize: "12px",
                    marginTop: "-8px",
                  }}
                >
                  Invitation link expires{" "}
                  {new Date(codeExpiresAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
              <Tooltip
                title={errors.password?.message || ""}
                open={!!errors.password}
                placement="top"
              >
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="auth-input pr-10"
                    placeholder="Password"
                    aria-invalid={!!errors.password}
                    {...register("password")}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center justify-center focus:outline-none"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    ) : (
                      <VisibilityOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    )}
                  </button>
                </div>
              </Tooltip>
            </div>

            <div className="flex flex-col gap-2">
              <p
                className={`flex items-center item-height ${successMessage ? "text-success" : "text-error"}`}
                style={{
                  visibility:
                    errorMessage || successMessage ? "visible" : "hidden",
                }}
              >
                {errorMessage || successMessage || "\u00A0"}
              </p>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth={true}
              >
                Continue
              </Button>
            </div>
          </form>

          <p
            className="text-center"
            style={{ color: "var(--color-fg-muted)", fontSize: "13px" }}
          >
            Already have an account?{" "}
            <button
              onClick={() => navigate("/")}
              className="cursor-pointer font-medium hover:underline"
              style={{ color: "var(--color-fg)", fontSize: "13px" }}
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};
